import React from "react";
import classNames from "classnames";
import { useSortable } from "@dnd-kit/sortable";
import { Counterparty } from "@common/types/counterparty";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import {
    RuleGroupType,
    TransactionRuleGroup,
    TransactionRule,
} from "@common/types/transactionRule";
import { getRuleTitle } from "../utils";
import styles from "../RulesTable.module.scss";
import { GroupHeaderCell } from "./GroupHeaderCell";
import { RuleRow } from "./RuleRow";
import { useHasDisabledAccount } from "./utils";

/**
 * RuleSortableRow wraps a rule row with sortable logic and group header cell.
 *
 * Warning flags (`isUnreachable` / `isDuplicate`) are computed once per group
 * in `RulesGroupBody` and passed down here as props to avoid each row
 * re-scanning the full group on every render.
 */
interface RuleSortableRowProps {
    readonly rule: TransactionRule;
    readonly ruleIndex: number;
    readonly group: TransactionRuleGroup;
    readonly overId: string | null;
    readonly groupListeners: React.HTMLAttributes<HTMLElement>;
    readonly groupAttributes: React.HTMLAttributes<HTMLElement>;
    readonly isGroupDragged: boolean;
    readonly counterparties: Counterparty[];
    readonly normalizedDescriptions: NormalizedDescriptionInfo[];
    readonly transactionCounts: Record<string, number>;
    readonly matchingCounts: Record<string, number>;
    readonly isCollapsed: boolean;
    readonly toggleCollapse: () => void;
    readonly priority?: number;
    readonly isUnreachable: boolean;
    readonly isDuplicate: boolean;
    readonly routeRuleId: string | null;
    readonly onRouteRuleClose: () => void;
}

export const RuleSortableRow: React.FC<RuleSortableRowProps> = ({
    rule,
    ruleIndex,
    group,
    overId,
    groupListeners,
    groupAttributes,
    isGroupDragged,
    counterparties,
    normalizedDescriptions,
    transactionCounts,
    matchingCounts,
    isCollapsed,
    toggleCollapse,
    priority,
    isUnreachable,
    isDuplicate,
    routeRuleId,
    onRouteRuleClose,
}) => {
    const isOverGroup =
        overId === group.id || group.rules?.some((r) => r.id === overId);
    const { attributes, listeners, setNodeRef } = useSortable({ id: rule.id });

    const hasDisabledAccount = useHasDisabledAccount(rule);

    if (isCollapsed && ruleIndex > 0) {
        return null;
    }

    return (
        <tr
            ref={setNodeRef}
            className={classNames(styles.rowHover, {
                [styles.unreachable]:
                    isUnreachable || rule.isDisabled || hasDisabledAccount,
            })}
            style={{ position: "relative" }}
        >
            {ruleIndex === 0 && (
                <GroupHeaderCell
                    group={group}
                    groupListeners={groupListeners}
                    groupAttributes={groupAttributes}
                    toggleCollapse={toggleCollapse}
                    isCollapsed={isCollapsed}
                    priority={priority}
                />
            )}

            {!isCollapsed ? (
                <RuleRow
                    rule={rule}
                    isOver={overId === rule.id && !isGroupDragged}
                    listeners={listeners}
                    attributes={attributes}
                    isGroupDragged={isGroupDragged}
                    counterparties={counterparties}
                    normalizedDescriptions={normalizedDescriptions}
                    transactionCounts={transactionCounts}
                    matchingCounts={matchingCounts}
                    isUnreachable={isUnreachable}
                    isDuplicate={isDuplicate}
                    hasDisabledAccount={hasDisabledAccount}
                    groupId={group.id}
                    groupType={group.type}
                    routeRuleId={routeRuleId}
                    onRouteRuleClose={onRouteRuleClose}
                />
            ) : (
                <td
                    colSpan={group.type === RuleGroupType.TRANSFER ? 4 : 5}
                    className={classNames(styles.td, styles.rulesCount)}
                >
                    {group.rules?.length} {getRuleTitle(group.type)}
                </td>
            )}
            {isOverGroup && isGroupDragged && ruleIndex === 0 && (
                <>
                    <div
                        className={classNames(
                            styles.insertionIndicator,
                            styles.top,
                            styles.withDot,
                        )}
                    />
                    <div
                        className={classNames(
                            styles.insertionIndicatorDot,
                            styles.top,
                        )}
                    />
                </>
            )}
        </tr>
    );
};
